// Payroll Service - unified interface for multi-country payroll calculations

import { Employee } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { 
  PayrollEngineFactory, 
  createPayrollEngine,
  CountryCode,
  PayrollInputData,
  PayrollCalculation,
  PayrollEngineError,
  ValidationError,
  CalculationError
} from './core';
import { OptimizedBatchProcessor, BatchProcessingOptions, BatchResult } from './services/OptimizedBatchProcessor';
import './engines'; // Import to trigger auto-registration

/**
 * Main service class for payroll operations
 * Provides a unified interface for all payroll calculations regardless of country
 */
export class PayrollService {
  private debugMode: boolean;

  constructor(debugMode: boolean = false) {
    this.debugMode = debugMode;
  }

  /**
   * Calculate payroll for an employee using country-specific engine
   */
  async calculatePayroll(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<PayrollCalculation> {
    try {
      // Determine country code
      const countryCode = await this.getEmployeeCountryCode(employee);
      
      // Create appropriate payroll engine
      const engine = createPayrollEngine(countryCode, this.debugMode);
      
      // Perform calculation
      const result = await engine.calculatePayroll(employee, inputData);
      
      // Log successful calculation
      if (this.debugMode) {
        console.log(`Payroll calculated successfully for ${employee.name} in ${countryCode}`);
        console.log(`Gross: €${result.grossSalary}, Net: €${result.netSalary}, Employer Cost: €${result.totalEmployerCost}`);
      }
      
      return result;
      
    } catch (error) {
      console.error('Payroll calculation failed:', error);
      
      if (error instanceof PayrollEngineError) {
        throw error;
      }
      
      throw new CalculationError(
        'UNKNOWN',
        `Payroll calculation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'service_level',
        { employee: employee.id, inputData }
      );
    }
  }

  /**
   * Validate payroll input data before calculation
   */
  async validatePayrollInput(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<{
    valid: boolean;
    errors: Record<string, string[]>;
    warnings: Record<string, string[]>;
  }> {
    try {
      const countryCode = await this.getEmployeeCountryCode(employee);
      const engine = createPayrollEngine(countryCode, this.debugMode);
      
      // Perform validation without calculation
      try {
        await (engine as any).validateInput(employee, inputData);
        return { valid: true, errors: {}, warnings: {} };
      } catch (error) {
        if (error instanceof ValidationError) {
          return {
            valid: false,
            errors: error.fieldErrors,
            warnings: {}
          };
        }
        throw error;
      }
      
    } catch (error) {
      return {
        valid: false,
        errors: { general: [(error instanceof Error) ? error.message : 'Unknown validation error'] },
        warnings: {}
      };
    }
  }

  /**
   * Get payroll constants for a country and year
   */
  async getPayrollConstants(countryCode: CountryCode, year: number) {
    try {
      const engine = createPayrollEngine(countryCode, this.debugMode);
      return await (engine as any).loadPayrollConstants(year);
    } catch (error) {
      throw new Error(`Failed to load payroll constants for ${countryCode} ${year}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get compliance rules for a country
   */
  async getComplianceRules(countryCode: CountryCode) {
    const { data, error } = await supabase
      .from('country_compliance_rules')
      .select('*')
      .eq('country_code', countryCode)
      .eq('is_active', true)
      .single();

    if (error) {
      throw new Error(`Failed to load compliance rules for ${countryCode}: ${error.message}`);
    }

    return data;
  }

  /**
   * Calculate payroll for multiple employees using optimized batch processing
   * @deprecated Use calculateBatchPayrollOptimized for better performance
   */
  async calculateBatchPayroll(
    employees: Employee[],
    inputDataMap: Record<string, PayrollInputData>,
    options: {
      stopOnError?: boolean;
      maxConcurrent?: number;
    } = {}
  ): Promise<{
    successful: Array<{ employee: Employee; result: PayrollCalculation }>;
    failed: Array<{ employee: Employee; error: Error }>;
  }> {
    // Forward to optimized implementation
    const optimizedOptions: BatchProcessingOptions = {
      stopOnError: options.stopOnError,
      maxConcurrent: options.maxConcurrent || 20, // Increased default from 5 to 20
      adaptiveConcurrency: true
    };
    
    const result = await this.calculateBatchPayrollOptimized(employees, inputDataMap, optimizedOptions);
    
    return {
      successful: result.successful.map(item => ({ 
        employee: item.employee, 
        result: item.result 
      })),
      failed: result.failed.map(item => ({ 
        employee: item.employee, 
        error: item.error 
      }))
    };
  }

  /**
   * Calculate payroll for multiple employees using optimized batch processing
   * Features: adaptive concurrency, retry logic, progress tracking, performance metrics
   */
  async calculateBatchPayrollOptimized(
    employees: Employee[],
    inputDataMap: Record<string, PayrollInputData>,
    options: BatchProcessingOptions = {}
  ): Promise<BatchResult<PayrollCalculation>> {
    const processor = new OptimizedBatchProcessor(options);
    
    // Create processor function that uses this service's calculatePayroll method
    const payrollProcessor = async (employee: Employee, inputData: PayrollInputData): Promise<PayrollCalculation> => {
      return await this.calculatePayroll(employee, inputData);
    };

    if (this.debugMode) {
      console.log(`Starting optimized batch payroll calculation for ${employees.length} employees`);
      console.log(`Initial concurrency: ${processor.getPerformanceMetrics().currentConcurrency}`);
    }

    const result = await processor.processBatch(employees, payrollProcessor, inputDataMap);
    
    if (this.debugMode) {
      const metrics = processor.getPerformanceMetrics();
      console.log('Batch processing completed:', {
        totalTime: result.metadata.totalProcessingTime,
        averageTime: result.metadata.averageProcessingTime,
        peakConcurrency: result.metadata.peakConcurrency,
        successRate: (result.successful.length / employees.length * 100).toFixed(1) + '%',
        totalRetries: result.metadata.totalRetries
      });
    }

    return result;
  }

  /**
   * Get supported countries and their capabilities
   */
  getSupportedCountries(): Record<string, any> {
    return PayrollEngineFactory.getEngineInfo();
  }

  /**
   * Check if a country is supported
   */
  isCountrySupported(countryCode: CountryCode): boolean {
    return PayrollEngineFactory.isCountrySupported(countryCode);
  }

  /**
   * Convert legacy payroll data to new format (migration helper)
   */
  async convertLegacyPayrollData(
    legacyData: any,
    countryCode: CountryCode
  ): Promise<PayrollInputData> {
    // Implementation would depend on the legacy format
    // This is a placeholder for future migration needs
    
    return {
      employeeId: legacyData.employee_id,
      countryCode,
      period: {
        start: legacyData.period_start,
        end: legacyData.period_end,
        month: new Date(legacyData.period_end).getMonth() + 1,
        year: new Date(legacyData.period_end).getFullYear(),
        countryCode
      },
      regularHours: legacyData.regular_hours || 0,
      overtimeHours: legacyData.overtime_hours || 0,
      specialHours: {
        night: legacyData.night_hours || 0,
        weekend: legacyData.weekend_hours || 0,
        holiday: legacyData.holiday_hours || 0
      },
      vacationDays: legacyData.vacation_days || 0,
      sickLeaveDays: legacyData.sick_leave_days || 0,
      otherAbsences: {
        paidLeave: legacyData.paid_leave_hours || 0,
        unpaidLeave: legacyData.unpaid_leave_hours || 0
      },
      bonuses: legacyData.bonuses || 0,
      commissions: legacyData.commissions || 0,
      otherIncome: legacyData.other_income || 0,
      advances: legacyData.advance_payment || 0,
      otherDeductions: {
        mealVouchers: legacyData.meal_vouchers_count || 0,
        other: legacyData.other_deductions || 0
      }
    };
  }

  /**
   * Helper methods
   */

  private async getEmployeeCountryCode(employee: Employee): Promise<CountryCode> {
    // Try to get country code from employee's business
    if (employee.business_id) {
      const { data: business } = await supabase
        .from('businesses')
        .select('country_code')
        .eq('id', employee.business_id)
        .single();
      
      if (business?.country_code) {
        return business.country_code.toUpperCase();
      }
    }
    
    // Fallback to Slovak if no country code found (for backward compatibility)
    console.warn(`No country code found for employee ${employee.id}, defaulting to SK`);
    return 'SK';
  }

  /**
   * Enable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Get debug mode status
   */
  isDebugMode(): boolean {
    return this.debugMode;
  }
}

// Export singleton instance for easy use
export const payrollService = new PayrollService();

// Export class for custom instances
export default PayrollService;